import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Paper,
  Switch,
  Divider,
  Button,
  Stack,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Notifications,
  NotificationsOff,
  AccessTime,
  EventNote,
  Warning,
  Campaign,
  DarkMode,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { apiRequest } from '../../utils/api';
import { MobileBottomNav } from '../common/MobileBottomNav';

export default function NotificationSettings() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [prefs, setPrefs] = useState({
    push_enabled: true,
    reminder_notifications: true,
    appointment_notifications: true,
    alert_notifications: true,
    system_notifications: true,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
  });

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/notifications/preferences');
      if (data.preferences) {
        const p = data.preferences;
        setPrefs({
          push_enabled: p.push_enabled ?? true,
          reminder_notifications: p.reminders_enabled ?? true,
          appointment_notifications: p.appointments_enabled ?? true,
          alert_notifications: p.alerts_enabled ?? true,
          system_notifications: p.system_enabled ?? true,
          quiet_hours_enabled: !!(p.quiet_hours_start && p.quiet_hours_end),
          quiet_hours_start: p.quiet_hours_start || '22:00',
          quiet_hours_end: p.quiet_hours_end || '08:00',
        });
      }
    } catch (err) {
      // Use defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleToggle = (field) => {
    setPrefs(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleTimeChange = (field, value) => {
    setPrefs(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      // Map frontend field names to backend field names
      const payload = {
        push_enabled: prefs.push_enabled,
        reminders_enabled: prefs.reminder_notifications,
        appointments_enabled: prefs.appointment_notifications,
        alerts_enabled: prefs.alert_notifications,
        system_enabled: prefs.system_notifications,
        quiet_hours_start: prefs.quiet_hours_enabled ? prefs.quiet_hours_start : null,
        quiet_hours_end: prefs.quiet_hours_enabled ? prefs.quiet_hours_end : null,
      };
      await apiRequest('/notifications/preferences', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setSuccess('Preferencias guardadas correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar las preferencias');
    } finally {
      setSaving(false);
    }
  };

  const settingItems = [
    {
      key: 'push_enabled',
      icon: <Notifications sx={{ color: '#2563EB' }} />,
      title: 'Notificaciones Push',
      description: 'Recibir notificaciones push en este dispositivo',
      master: true,
    },
    {
      key: 'reminder_notifications',
      icon: <AccessTime sx={{ color: '#F59E0B' }} />,
      title: 'Recordatorios',
      description: 'Notificaciones de recordatorios programados',
    },
    {
      key: 'appointment_notifications',
      icon: <EventNote sx={{ color: '#2563EB' }} />,
      title: 'Citas Médicas',
      description: 'Avisos sobre próximas citas y cambios',
    },
    {
      key: 'alert_notifications',
      icon: <Warning sx={{ color: '#EF4444' }} />,
      title: 'Alertas',
      description: 'Alertas proactivas del sistema de salud',
    },
    {
      key: 'system_notifications',
      icon: <Campaign sx={{ color: '#10B981' }} />,
      title: 'Notificaciones del Sistema',
      description: 'Actualizaciones y avisos generales',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: 10, md: 4 } }}>
      <Container maxWidth="md" sx={{ pt: { xs: 2, md: 4 } }}>
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h2">Notificaciones</Typography>
              <Typography variant="body2" color="text.secondary">
                Gestiona cómo y cuándo recibes notificaciones
              </Typography>
            </Box>
          </Stack>
        </motion.div>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Notification Types */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Paper sx={{ p: 0, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h4">Tipos de Notificación</Typography>
            </Box>
            {settingItems.map((item, index) => (
              <React.Fragment key={item.key}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    opacity: !item.master && !prefs.push_enabled ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
                    {item.icon}
                    <Box>
                      <Typography variant="body1" fontWeight={600}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                    </Box>
                  </Stack>
                  <Switch
                    checked={prefs[item.key]}
                    onChange={() => handleToggle(item.key)}
                    disabled={!item.master && !prefs.push_enabled}
                    color="primary"
                  />
                </Box>
                {index < settingItems.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Paper>
        </motion.div>

        {/* Quiet Hours */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Paper sx={{ p: 0, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DarkMode sx={{ color: '#6366F1' }} />
                  <Typography variant="h4">Horas de Silencio</Typography>
                </Stack>
                <Switch
                  checked={prefs.quiet_hours_enabled}
                  onChange={() => handleToggle('quiet_hours_enabled')}
                  color="primary"
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No recibir notificaciones en este horario
              </Typography>
            </Box>
            {prefs.quiet_hours_enabled && (
              <Box sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <TextField
                    label="Desde"
                    type="time"
                    value={prefs.quiet_hours_start}
                    onChange={(e) => handleTimeChange('quiet_hours_start', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    size="small"
                    sx={{ width: { xs: '100%', sm: 160 } }}
                  />
                  <Typography variant="body2" color="text.secondary">hasta</Typography>
                  <TextField
                    label="Hasta"
                    type="time"
                    value={prefs.quiet_hours_end}
                    onChange={(e) => handleTimeChange('quiet_hours_end', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    size="small"
                    sx={{ width: { xs: '100%', sm: 160 } }}
                  />
                </Stack>
              </Box>
            )}
          </Paper>
        </motion.div>

        {/* Status */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h4" gutterBottom>Estado</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                icon={prefs.push_enabled ? <Notifications /> : <NotificationsOff />}
                label={prefs.push_enabled ? 'Push activas' : 'Push desactivadas'}
                color={prefs.push_enabled ? 'success' : 'default'}
                variant="outlined"
              />
              {prefs.quiet_hours_enabled && (
                <Chip
                  size="small"
                  icon={<DarkMode />}
                  label={`Silencio: ${prefs.quiet_hours_start} - ${prefs.quiet_hours_end}`}
                  color="info"
                  variant="outlined"
                />
              )}
              {user?.role && (
                <Chip
                  size="small"
                  label={`Rol: ${user.role}`}
                  variant="outlined"
                />
              )}
            </Stack>
          </Paper>
        </motion.div>

        {/* Save Button */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleSave}
            disabled={saving}
            sx={{ py: 1.5, mb: 2 }}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Guardar Preferencias'}
          </Button>
        </motion.div>
      </Container>

      <MobileBottomNav />
    </Box>
  );
}
